import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Page {
  id: string;
  name: string;
  path: string;
  is_visible: boolean;
  display_order: number;
}

export default function Navigation() {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('is_visible', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      
      const navPages = (data || []).filter(page => page.path !== '/');
      setPages(navPages);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-12 sm:h-16 w-auto"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {loading ? (
              <div className="text-gray-400">Loading...</div>
            ) : (
              <>
                {pages.map((page) => (
                  <Link
                    key={page.id}
                    to={page.path}
                    className="text-gray-600 hover:text-gray-900 font-medium cursor-pointer"
                  >
                    {page.name}
                  </Link>
                ))}
                <Link 
                  to="/contact" 
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Schedule Demo
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            {loading ? (
              <div className="text-gray-400 px-4">Loading...</div>
            ) : (
              <div className="flex flex-col space-y-3">
                {pages.map((page) => (
                  <Link
                    key={page.id}
                    to={page.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-gray-600 hover:text-gray-900 font-medium px-4 py-2 hover:bg-gray-50 rounded-md cursor-pointer"
                  >
                    {page.name}
                  </Link>
                ))}
                <Link 
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-center cursor-pointer mx-4"
                >
                  Schedule Demo
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
